<!--
  DocumentTiles — the search result as preview tiles (Spec 120 E2).
  Every tile is a real link, so middle click / context menu open a new tab.
  No selection and no bulk actions here: those live in the table only (E8).
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { navigate } from 'svelte-routing';
  import { FileText } from 'lucide-svelte';
  import type { Document } from '../api';
  import { userSettings } from '../stores/settingsStore';
  import { formatDate } from '../settings';
  import { Spinner, PaginationBar } from './ui/index';
  import DocumentThumbnail from './DocumentThumbnail.svelte';

  export let documents: Document[];
  export let size: 'large' | 'small' = 'large';
  export let loading = false;
  export let hasMore = false;
  export let hasPrev = false;
  export let page = 0;
  export let pageSize = 20;

  const dispatch = createEventDispatcher<{ pageChange: number; pageSizeChange: number }>();

  function open(e: MouseEvent, doc: Document) {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(`/documents/${doc.slug || doc.id}`);
  }
</script>

<div
  data-testid="browse-tiles"
  data-size={size}
  class="bg-surface border border-border rounded-lg overflow-hidden"
>
  {#if loading}
    <div data-testid="browse-loading" class="flex justify-center py-12">
      <Spinner size="lg" label={$_('messages.loading')} />
    </div>
  {:else if documents.length === 0}
    <div class="flex flex-col items-center justify-center py-12 px-4 text-center">
      <FileText class="w-8 h-8 text-text-muted mb-2" strokeWidth={1.5} />
      <p class="text-sm text-text-secondary">{$_('components.document_table.empty_state')}</p>
    </div>
  {:else}
    <div
      class="grid gap-4 p-4 {size === 'large'
        ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
        : 'grid-cols-3 md:grid-cols-5 xl:grid-cols-8'}"
    >
      {#each documents as doc (doc.id)}
        {@const title = doc.displayName || doc.fileName}
        <a
          data-testid="document-tile"
          href="/documents/{doc.slug || doc.id}"
          class="flex flex-col gap-2 min-w-0 text-text no-underline rounded-lg p-1 hover:bg-surface-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          on:click={(e) => open(e, doc)}
        >
          <DocumentThumbnail document={doc} size="medium" alt="" lazy />
          <span class="text-sm text-text truncate" {title}>{title}</span>
          <span class="text-xs text-text-muted">
            {formatDate(doc.referenceDate || doc.uploadedAt, $userSettings)}
          </span>
        </a>
      {/each}
    </div>

    <PaginationBar
      {page}
      {pageSize}
      {hasMore}
      {hasPrev}
      on:pageChange={(e) => dispatch('pageChange', e.detail)}
      on:pageSizeChange={(e) => dispatch('pageSizeChange', e.detail)}
    />
  {/if}
</div>
